import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { PROCEDURE_STEPS } from '@/components/popups/AddProcedurePopUp/constants';

import { STORAGE_KEYS } from '@/atoms/storageKeys';

// ============================================================================
// Edit Plan Draft - tracks open modals/forms in EditPlanView
// ============================================================================

export interface EditPlanDraft {
  planId: string;
  showAddQuiz: boolean;
  quizSectionId: string;
  quizIsInSection: boolean;
  showAddTask: boolean;
  taskValues: {
    task: string;
    notes: string;
    link: string;
  };
  showAddSection: boolean;
  addSectionText: string;
  estimatedMinutes: string;
  description: string;
  savedAt: number;
}

const isEditPlanDraft = (data: unknown): data is EditPlanDraft => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return typeof obj.planId === 'string' && typeof obj.savedAt === 'number';
};

const editPlanDraftStorage = createJSONStorage<EditPlanDraft | null>(
  () => localStorage,
);

const validatedEditPlanStorage: typeof editPlanDraftStorage = {
  ...editPlanDraftStorage,
  getItem: (key: string, initialValue: EditPlanDraft | null) => {
    const raw = localStorage.getItem(key);
    if (!raw) return initialValue;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isEditPlanDraft(parsed)) {
        localStorage.removeItem(key);
        return initialValue;
      }
      return parsed;
    } catch {
      localStorage.removeItem(key);
      return initialValue;
    }
  },
  setItem: (key: string, value: EditPlanDraft | null) => {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key);
  },
};

export const editPlanDraftAtom = atomWithStorage<EditPlanDraft | null>(
  STORAGE_KEYS.EDIT_PLAN_DRAFT,
  null,
  validatedEditPlanStorage,
  { getOnInit: true },
);

// ============================================================================
// Procedure Draft - tracks AddProcedurePopup state
// ============================================================================

export interface ProcedureDraft {
  activeStep: number;
  selectedDocuments: unknown[];
  planId?: string | null;
  sectionId?: string;
  isInSection?: boolean;
  savedAt: number;
  search?: unknown;
  categorySelected?: unknown;
  categoryName?: string;
  preservedSubcategoryIds?: string[];
  previewDocument?: unknown;
}

export const DEFAULT_PROCEDURE_DRAFT: Omit<ProcedureDraft, 'savedAt'> = {
  activeStep: PROCEDURE_STEPS.CATEGORIES,
  search: {
    displayed: '',
    query: '',
  },
  selectedDocuments: [],
  categorySelected: null,
  categoryName: '',
  preservedSubcategoryIds: [],
  previewDocument: null,
  planId: null,
  sectionId: '',
  isInSection: false,
};

const isProcedureDraft = (data: unknown): data is ProcedureDraft => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.activeStep === 'number' &&
    typeof obj.savedAt === 'number' &&
    Array.isArray(obj.selectedDocuments)
  );
};

const procedureDraftStorage = createJSONStorage<ProcedureDraft | null>(
  () => localStorage,
);

const validatedProcedureStorage: typeof procedureDraftStorage = {
  ...procedureDraftStorage,
  getItem: (key: string, initialValue: ProcedureDraft | null) => {
    const raw = localStorage.getItem(key);
    if (!raw) return initialValue;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isProcedureDraft(parsed)) {
        localStorage.removeItem(key);
        return initialValue;
      }
      return parsed;
    } catch {
      localStorage.removeItem(key);
      return initialValue;
    }
  },
  setItem: (key: string, value: ProcedureDraft | null) => {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key);
  },
};

export const procedureDraftAtom = atomWithStorage<ProcedureDraft | null>(
  STORAGE_KEYS.PROCEDURE_DRAFT,
  null,
  validatedProcedureStorage,
  { getOnInit: true },
);

export const procedureDraftPlanIdAtom = atom(get => {
  const draft = get(procedureDraftAtom);
  if (!draft) return null;

  const hasProgress =
    draft.selectedDocuments.length > 0 || draft.activeStep > 1;
  return hasProgress ? draft.planId : null;
});

export const procedureDraftSectionIdAtom = atom(get => {
  const draft = get(procedureDraftAtom);
  return draft?.sectionId ?? '';
});

export const procedureDraftIsInSectionAtom = atom(get => {
  const draft = get(procedureDraftAtom);
  return draft?.isInSection ?? false;
});
